/**
 * What to tell a reader when generation was attempted and gave up.
 *
 * The read API reports a `reason` on every FAILED response, in the generator's own vocabulary:
 * REJECTED, TRUNCATED, MODEL_TIMEOUT, THROTTLED, STALE_INDEX, and UNKNOWN where the row
 * recorded none. That vocabulary is operator-facing and is never shown. What this does instead
 * is group it by the only thing a reader can act on — whether to wait, whether to ask for
 * another attempt, or whether this audit will simply never have an insight.
 *
 * Three readings, not six strings. Two reasons that read identically to a reader are the same
 * reading: a truncation and a timeout both mean the audit was too big to finish, and a throttle
 * and a stale index both mean the attempts ran out rather than the audit being at fault. A
 * distinction a reader cannot act on would only add a string to translate.
 */
import { MessageBannerType } from '@amzn/stencil-react-components/message-banner';

import type { InsightBanner } from './AuditInsightsFallback';

/**
 * The content check stopped generation.
 *
 * Final, and the banner says so. This is settled by the audit's own wording, so every
 * redelivery was refused in the same way and asking for another attempt cannot change it.
 */
const CONTENT: InsightBanner = {
  titleKey: 'failedTitle',
  bodyKey: 'failedContentText',
  type: MessageBannerType.Error,
};

/**
 * The audit was too large for generation to finish.
 *
 * `TRUNCATED` ran past the output limit and `MODEL_TIMEOUT` ran past the time one, which are
 * two limits on the same cause. The one failed reading whose cause is visible to the reader —
 * the question count is on the screen below — so it is named rather than left generic.
 */
const SIZE: InsightBanner = {
  titleKey: 'failedTitle',
  bodyKey: 'failedSizeText',
  type: MessageBannerType.Error,
};

/**
 * Generation ran out of attempts rather than failing on the audit.
 *
 * The only reading where a later attempt could succeed, so this is the one that does not call
 * the outcome final. Warning rather than error: nothing about the audit is wrong.
 */
const TRANSIENT: InsightBanner = {
  titleKey: 'failedTitle',
  bodyKey: 'failedTransientText',
  type: MessageBannerType.Warning,
};

/**
 * A reason this build has not learned, or none the row recorded.
 *
 * The read API substitutes `UNKNOWN` when the row holds no reason, so this is reached often
 * enough to be a real state rather than a guard. Says only what holds for every failure.
 */
const UNREAD: InsightBanner = {
  titleKey: 'failedTitle',
  bodyKey: 'failedText',
  type: MessageBannerType.Error,
};

// A Map rather than an indexed object: the lookup key is a variable, which an object index
// would make a lint finding. Keys are the generator's own strings, which arrive as a free
// `String` on the wire rather than a closed enum — so an unrecognised one has to read as
// UNREAD rather than as nothing at all.
const REASONS = new Map<string, InsightBanner>([
  ['REJECTED', CONTENT],
  ['TRUNCATED', SIZE],
  ['MODEL_TIMEOUT', SIZE],
  ['THROTTLED', TRANSIENT],
  ['STALE_INDEX', TRANSIENT],
]);

/**
 * The banner for an audit whose insight generation gave up.
 *
 * @param reason - The reason the read API reported. Absent on a response that omitted it.
 */
export function failedReason(reason: string | undefined): InsightBanner {
  return (reason && REASONS.get(reason)) || UNREAD;
}
